import { useState, useEffect } from 'react';
import { CalendarEventDto } from '@/types/calendar';
import { getCalendarEvents } from '@/services/calendar.service';
import { useAuth } from '@/hooks/useAuth';
import { AnyEvent, EventType, ExpertEvent, Expert, MockEvent } from './types';
import { MOCK_EVENTS } from './constants';

// ─── Mapper ───────────────────────────────────────────────────────────────────

const KNOWN_CODES: EventType[] = ['food', 'workout', 'liquid', 'feeling', 'medication', 'task', 'appointment'];

const CODE_ALIASES: Record<string, EventType> = { fluid: 'liquid' };

const toEventType = (code: string): EventType =>
  CODE_ALIASES[code] ?? (KNOWN_CODES.includes(code as EventType) ? (code as EventType) : 'task');

const toTime = (iso: string | null): string => {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
};

const EXPERT_COLORS = ['#1565c0', '#2e7d32', '#6a1b9a', '#c62828', '#00796b', '#e65100', '#4527a0'];

const expertColor = (id: number): string => EXPERT_COLORS[id % EXPERT_COLORS.length];

const toInitials = (name: string): string =>
  name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

export const mapApiEvent = (dto: CalendarEventDto, currentUserId: number): AnyEvent => {
  const isExpert = dto.createdBy.id !== currentUserId;
  const time = toTime(dto.scheduledLdt);
  const type = toEventType(dto.eventType.code);

  if (isExpert) {
    const expert: Expert = {
      name: dto.createdBy.name,
      initials: toInitials(dto.createdBy.name),
      color: expertColor(dto.createdBy.id),
    };
    const event: ExpertEvent = {
      id: dto.id, time, type, title: dto.title,
      completed: dto.isCompleted, fromExpert: true, expert, raw: dto,
    };
    return event;
  }

  const event: MockEvent = {
    id: dto.id, time, type, title: dto.title,
    completed: dto.isCompleted, fromExpert: false, raw: dto,
  };
  return event;
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

interface UseEventsForDateResult {
  events: AnyEvent[];
  loading: boolean;
  error: string | null;
  useMock: boolean;
  toggleMock: () => void;
  refetch: () => void;
}

export const useEventsForDate = (date: string): UseEventsForDateResult => {
  const { user } = useAuth();
  const [useMock, setUseMock] = useState(() => localStorage.getItem('dashboard-use-mock') === 'true');
  const [events, setEvents] = useState<AnyEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    if (useMock) {
      setEvents(MOCK_EVENTS);
      setError(null);
      return;
    }

    let cancelled = false;
    const fetchEvents = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getCalendarEvents({
          dateFrom: date,
          dateTo: date,
          limit: 500,
        });
        if (!cancelled) {
          setEvents(data.map(dto => mapApiEvent(dto, user?.id ?? 0)));
        }
      } catch {
        if (!cancelled) {
          setError('Не вдалося завантажити події');
          setEvents([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchEvents();
    return () => { cancelled = true; };
  }, [date, useMock, revision]);

  return {
    events, loading, error, useMock,
    toggleMock: () => setUseMock(v => { const next = !v; localStorage.setItem('dashboard-use-mock', String(next)); return next; }),
    refetch: () => setRevision(v => v + 1),
  };
};
